import {
  jalaaliMonthLength,
  toGregorian,
  toJalaali,
} from 'jalaali-js';

export type JalaliParts = { jy: number; jm: number; jd: number };

const PERSIAN_MONTHS_FA = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

const PERSIAN_MONTHS_EN = [
  'Farvardin',
  'Ordibehesht',
  'Khordad',
  'Tir',
  'Mordad',
  'Shahrivar',
  'Mehr',
  'Aban',
  'Azar',
  'Dey',
  'Bahman',
  'Esfand',
] as const;

const WEEKDAYS_FA = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const;
const WEEKDAYS_EN = ['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'] as const;
const WEEKDAYS_FULL_FA = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'] as const;
const WEEKDAYS_FULL_EN = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;

export function parseIsoDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  if (
    date.getUTCFullYear() !== y ||
    date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== d
  ) {
    return null;
  }
  return date;
}

export function toIsoDate(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isoToJalali(iso: string): JalaliParts | null {
  const date = parseIsoDate(iso);
  if (!date) return null;
  return toJalaali(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

export function jalaliToIso(jy: number, jm: number, jd: number): string {
  const g = toGregorian(jy, jm, jd);
  return `${g.gy}-${String(g.gm).padStart(2, '0')}-${String(g.gd).padStart(2, '0')}`;
}

export function todayIso(): string {
  const now = new Date();
  return toIsoDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

export function addJalaliMonths(jy: number, jm: number, delta: number): { jy: number; jm: number } {
  const index = jy * 12 + (jm - 1) + delta;
  return {
    jy: Math.floor(index / 12),
    jm: (index % 12) + 1,
  };
}

/** Saturday = 0 … Friday = 6 (Iranian week) */
export function jalaliWeekday(jy: number, jm: number, jd: number): number {
  const g = toGregorian(jy, jm, jd);
  const dow = new Date(Date.UTC(g.gy, g.gm - 1, g.gd)).getUTCDay(); // Sun=0
  return (dow + 1) % 7;
}

export function monthLength(jy: number, jm: number): number {
  return jalaaliMonthLength(jy, jm);
}

export function monthName(jm: number, isFa: boolean): string {
  return isFa ? PERSIAN_MONTHS_FA[jm - 1] : PERSIAN_MONTHS_EN[jm - 1];
}

export function weekdayLabels(isFa: boolean): readonly string[] {
  return isFa ? WEEKDAYS_FA : WEEKDAYS_EN;
}

/** Full weekday for an ISO date. Saturday is the first day of the Iranian week. */
export function weekdayName(iso: string, isFa: boolean): string {
  const j = isoToJalali(iso);
  if (!j) return '';
  const names = isFa ? WEEKDAYS_FULL_FA : WEEKDAYS_FULL_EN;
  return names[jalaliWeekday(j.jy, j.jm, j.jd)] ?? '';
}

export function toFaDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

const GREGORIAN_MONTHS_FA = [
  'ژانویه',
  'فوریه',
  'مارس',
  'آوریل',
  'مه',
  'ژوئن',
  'ژوئیه',
  'اوت',
  'سپتامبر',
  'اکتبر',
  'نوامبر',
  'دسامبر',
] as const;

const GREGORIAN_MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export function gregorianMonthName(month: number, isFa: boolean): string {
  return isFa ? GREGORIAN_MONTHS_FA[month - 1] : GREGORIAN_MONTHS_EN[month - 1];
}

export function gregorianMonthLength(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Saturday = 0 … Friday = 6, matching the Iranian week. */
export function gregorianWeekday(year: number, month: number, day: number): number {
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return (dow + 1) % 7;
}

export function formatJalaliNumeric(iso: string, isFa: boolean): string {
  const j = isoToJalali(iso);
  if (!j) return '';
  const raw = `${j.jy}/${String(j.jm).padStart(2, '0')}/${String(j.jd).padStart(2, '0')}`;
  return isFa ? toFaDigits(raw) : raw;
}

export function formatGregorianNumeric(iso: string, isFa: boolean): string {
  if (!parseIsoDate(iso)) return '';
  const raw = iso.replace(/-/g, '/');
  return isFa ? toFaDigits(raw) : raw;
}

export function formatJalaliDisplay(iso: string, isFa: boolean): string {
  const j = isoToJalali(iso);
  if (!j) return '';
  const day = String(j.jd).padStart(2, '0');
  const month = monthName(j.jm, isFa);
  const year = String(j.jy);
  const raw = `${day} ${month} ${year}`;
  return isFa ? toFaDigits(raw) : raw;
}

export function compareIso(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
